import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  TextField,
  Chip,
  Typography,
  Grid2,
  CircularProgress,
} from "@mui/material";
import { Checkbox } from "antd";
import CloseIcon from "@mui/icons-material/Close";
import CustomIcon from "../../../Components/CustomIcon";
import { getChipShortForm } from "./CommonFunctions";
import debounce from "lodash.debounce";

const isAllPath = (path) => {
  const parts = path.split(".");
  return parts[parts.length - 2]?.endsWith("all") ?? false;
};

export default function HierarchicalSelector({
  data,
  selectedChips = [],
  onSelectionChange,
  onDuplicateError,
  maxChips = 50,
  allowDuplicates = false,
  maxHeight = '450px',
  placeholder = 'Define entities & corresponding signals',
  isLoading = false,
  selectionMode = "default",
}) {
  const [inputValue, setInputValue] = useState("");
  const [chips, setChips] = useState([]);
  const [error, setError] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [invalidChips, setInvalidChips] = useState([])

  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const highlightedItemRef = useRef(null);

  // flatten data
  const flattenData = useCallback((node, prefix = "", depth = 0) => {
    let paths = [];
    const currentPath = prefix ? `${prefix}.${node.name}` : node.name;
    if (node.type === "normal") {
      paths.push({
        path: currentPath,
        name: node.name,
        fullPath: currentPath,
        depth,
        type: node.type,
      });
    }
    if (node.type === "complex" && node.children) {
      Object.values(node.children).forEach((child) => {
        paths = paths.concat(flattenData(child, currentPath, depth + 1));
      });
    }
    return paths;
  }, []);

  const allOptions = useMemo(() => {
    return Object.values(data || {}).flatMap((node) => flattenData(node));
  }, [data, flattenData]);

  const getOptionByPath = useCallback(
    (path) => allOptions.find((opt) => opt.fullPath === path),
    [allOptions]
  );


  // Returns all individual sibling paths for a given "all" path
  // e.g. batterysystem.stringall.soc → [batterysystem.string1.soc, batterysystem.string2.soc, ...]
  const getExpandedPaths = useCallback((allPath) => {
    const parts = allPath.split(".");
    const nodeName = parts[parts.length - 2];
    const signal = parts[parts.length - 1];
    const base = nodeName.replace(/all$/, "");
    const parentPath = parts.slice(0, -2).join(".");

    if (!base) return [];

    return allOptions
      .filter((o) => {
        const segs = o.fullPath.split(".");
        const siblingNode = segs[segs.length - 2];
        return (
          segs.slice(0, -2).join(".") === parentPath &&
          siblingNode.startsWith(base) &&
          !siblingNode.endsWith("all") &&
          segs[segs.length - 1] === signal
        );
      })
      .map((o) => o.fullPath);
  }, [allOptions]);

  // Returns "checked" | "indeterminate" | "unchecked" for a given "all" path
  const getAllNodeState = useCallback((allPath) => {
    const siblings = getExpandedPaths(allPath);
    if (siblings.length === 0) return "unchecked";
    const selectedCount = siblings.filter((p) => chips.includes(p)).length;
    if (selectedCount === siblings.length) return "checked";
    if (selectedCount > 0) return "indeterminate";
    return "unchecked";
  }, [getExpandedPaths, chips]);

  // Tri-state toggle for "all" nodes in checklist mode.
  // checked → deselect all individuals
  // unchecked or indeterminate → select all missing individuals
  // Keeps the dropdown open (unlike handleAddChip).
  const handleAllNodeToggle = useCallback((allPath) => {
    setError("");
    const siblings = getExpandedPaths(allPath);
    const selectedCount = siblings.filter((p) => chips.includes(p)).length;
    const state =
      selectedCount === 0 ? "unchecked" :
      selectedCount === siblings.length ? "checked" :
      "indeterminate";

    if (state === "checked") {
      const updatedChips = chips.filter((c) => !siblings.includes(c));
      setChips(updatedChips);
      onSelectionChange?.(updatedChips);
      return;
    }

    const missing = siblings.filter((p) => !chips.includes(p));
    if (missing.length === 0) return;
    if (chips.length + missing.length >= maxChips) {
      setError(`Maximum ${maxChips} selections allowed`);
      return;
    }
    const updatedChips = [...chips, ...missing];
    setChips(updatedChips);
    onSelectionChange?.(updatedChips);
  }, [getExpandedPaths, chips, maxChips, onSelectionChange]);

  const handleChecklistToggleChip = useCallback((chipPath) => {
    setError("");
    if (chips.includes(chipPath)) {
      const updatedChips = chips.filter((chip) => chip !== chipPath);
      setChips(updatedChips);
      onSelectionChange?.(updatedChips);
      return;
    }
    if (chips.length >= maxChips) {
      setError(`Maximum ${maxChips} selections allowed`);
      return;
    }
    const updatedChips = [...chips, chipPath];
    setChips(updatedChips);
    onSelectionChange?.(updatedChips);
  }, [chips, maxChips, onSelectionChange]);

  const handleAddChip = useCallback(
    (value) => {
      const values = value
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);

      let newChips = [];
      let duplicates = [];

      values.forEach((val) => {
        const parts = val.split(".");
        const signal = parts[parts.length - 1];
        const nodeName = parts[parts.length - 2];

        if (nodeName?.endsWith("all")) {
          // Skip validating the "all" path directly
          const base = nodeName.replace(/all$/, "");
          const parentPath = parts.slice(0, -2).join(".");

          const expanded = allOptions
            .filter((o) => {
              const segs = o.fullPath.split(".");
              const siblingNode = segs[segs.length - 2];

              return (
                segs.slice(0, -2).join(".") === parentPath && // same parent
                siblingNode.startsWith(base) &&               // string1, pack3...
                !siblingNode.endsWith("all") &&               // exclude "stringall"
                segs[segs.length - 1] === signal              // same signal
              );
            })
            .map((o) => o.fullPath);

          if (expanded.length === 0) {
            // mark the original "all" value invalid
            setInvalidChips((prev) => [...prev, val]);
            return;
          }

          expanded.forEach((expandedVal) => {
            if (chips.includes(expandedVal)) {
              return; // already selected — silently skip for "all" expansions
            } else if (chips.length + newChips.length >= maxChips) {
              setError(`Maximum ${maxChips} selections allowed`);
              return;
            } else {
              newChips.push(expandedVal);
            }
          });

          return; // stop here, don't let "all" itself go further
        }

        // For non-all nodes, check normally
        const opt = getOptionByPath(val);
        if (!opt) {
          setInvalidChips((prev) => [...prev, val]);
          return;
        }

        if (!allowDuplicates && chips.includes(val)) {
          duplicates.push(val);
        } else if (chips.length + newChips.length >= maxChips) {
          setError(`Maximum ${maxChips} selections allowed`);
          return;
        } else {
          newChips.push(val);
        }
      });

      if (duplicates.length > 0) {
        onDuplicateError?.();
      }

      if (newChips.length > 0) {
        const updatedChips = [...chips, ...newChips];
        setChips(updatedChips);
        onSelectionChange?.(updatedChips);
      }

      setInputValue("");
      setSuggestions([]);
      setHighlightIndex(-1);
      setShowSuggestions(false);
    },
    [chips, getOptionByPath, allOptions, allowDuplicates, maxChips, onSelectionChange]
  );



  const handleRemoveChip = useCallback(
    (chipToRemove) => {
      const updated = chips.filter((c) => c !== chipToRemove);
      setChips(updated);
      onSelectionChange?.(updated);
    },
    [chips, onSelectionChange]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        const highlightedSuggestion = suggestions[highlightIndex];
        if (highlightedSuggestion) {
          if (selectionMode === "checklist" && isAllPath(highlightedSuggestion.fullPath)) {
            handleAllNodeToggle(highlightedSuggestion.fullPath);
          } else if (selectionMode === "checklist") {
            handleChecklistToggleChip(highlightedSuggestion.fullPath);
          } else {
            handleAddChip(highlightedSuggestion.fullPath);
          }
          return;
        }
        if (inputValue.trim()) {
          if (selectionMode === "checklist" && isAllPath(inputValue.trim())) {
            handleAllNodeToggle(inputValue.trim());
            return;
          }
          if (selectionMode === "checklist") {
            const exactMatch = allOptions.find(
              (opt) => opt.fullPath === inputValue.trim()
            );
            if (exactMatch) {
              handleChecklistToggleChip(exactMatch.fullPath);
              return;
            }
          }
          handleAddChip(inputValue);
        }
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        if (suggestions.length > 0) {
          setHighlightIndex((prev) =>
            prev < suggestions.length - 1 ? prev + 1 : 0
          );
        }
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (suggestions.length > 0) {
          setHighlightIndex((prev) =>
            prev > 0 ? prev - 1 : suggestions.length - 1
          );
        }
      } else if (e.key === "Escape") {
        setShowSuggestions(false);
        setSuggestions([]);
        setHighlightIndex(-1);
        inputRef.current?.blur();
      }
    },
    [
      inputValue,
      highlightIndex,
      suggestions,
      handleAddChip,
      handleAllNodeToggle,
      handleChecklistToggleChip,
      selectionMode,
      allOptions,
    ]
  );

  const debouncedFilter = useCallback(
    debounce((val) => {
      if (val.trim()) {
        const filtered = [];
        let count = 0;
        for (const opt of allOptions) {
          if (
            opt.fullPath.toLowerCase().includes(val.toLowerCase()) ||
            opt.name.toLowerCase().includes(val.toLowerCase())
          ) {
            filtered.push(opt);
            count++;
            if (count >= 100) break;
          }
        }
        setSuggestions(filtered);
        setShowSuggestions(true);
        setHighlightIndex(-1);
      } else {
        setSuggestions(allOptions.slice(0, 100));
        setShowSuggestions(true);
      }
    }, 200),
    [allOptions]
  );

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    debouncedFilter(val);
  };

  const handleSuggestionClick = (s) => {
    if (selectionMode === "checklist" && isAllPath(s.fullPath)) {
      handleAllNodeToggle(s.fullPath);
      return;
    }
    if (selectionMode === "checklist") {
      handleChecklistToggleChip(s.fullPath);
      return;
    }
    handleAddChip(s.fullPath);
  };

  const onClearAll = () => {
    setChips([]);
    setInvalidChips([]);
    onSelectionChange?.([]);
  }

  useEffect(() => {
    setChips(selectedChips);
  }, [selectedChips]);

  // Auto-scroll highlighted item into view when using keyboard navigation
  useEffect(() => {
    if (highlightIndex >= 0 && highlightedItemRef.current) {
      highlightedItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [highlightIndex]);

  // Show dropdown when loading finishes and there's text in input
  useEffect(() => {
    if (!isLoading && inputValue.trim() && isFocused) {
      debouncedFilter(inputValue);
    }
  }, [isLoading, inputValue, isFocused, debouncedFilter]);

  return (
    <Grid2 container gap={0.5} flexDirection={'column'} sx={{ width: "100%" }}>
      <Box sx={{position: "relative"}}>
        <TextField
          inputRef={inputRef}
          size="small"
          fullWidth
          variant="outlined"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            setIsFocused(true);
            if (!inputValue.trim()) {
              setSuggestions(allOptions.slice(0, 100));
              setShowSuggestions(true);
              setHighlightIndex(-1);
            }
          }}
          onBlur={() => setTimeout(() => {
            setIsFocused(false);
            setShowSuggestions(false);
            setSuggestions([]);
            setHighlightIndex(-1);
          }, 200)}
          error={!!error}
          autoComplete="off"
          InputProps={{
            endAdornment: isLoading ? (
              <CircularProgress size={16} sx={{ color: '#6D72F6' }} />
            ) : null,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              fontSize: "12px",
              lineHeight: "24px",
              "& fieldset": {
                borderColor: "#E6E6E6",
                borderWidth: "1px",
                borderRadius: '8px',
              },
              "&:hover fieldset": {
                borderColor: "#6D72F6",
                borderWidth: "1px",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#6D72F6",
                borderWidth: "1px",
              },
              "& input::placeholder": {
                fontSize: "12px",
                lineHeight: "24px",
                color: "#9e9e9e",
                opacity: 1,
              },
            },
          }}
        />

        {/* Suggestions */}
        {showSuggestions && suggestions.length > 0 && (
          <Box
            ref={suggestionsRef}
            sx={{
              position: "absolute",
              zIndex: 9999,
              width: "100%",
              maxHeight: 400,
              overflowY: "auto",
              borderRadius: 2,
              boxShadow: 4,
              backgroundColor: '#FFFFFF'
            }}
          >
            <Grid2 container flexDirection="column">
              {suggestions.map((s, idx) => {
                const allState = selectionMode === "checklist" && isAllPath(s.fullPath)
                  ? getAllNodeState(s.fullPath)
                  : null;
                return (
                  <Box
                    key={s.fullPath}
                    ref={idx === highlightIndex ? highlightedItemRef : null}
                    onMouseDown={(e) => {
                      if (selectionMode === "checklist") {
                        e.preventDefault();
                      }
                      handleSuggestionClick(s);
                    }}
                    sx={{
                      alignItems: selectionMode === "checklist" ? "center" : "flex-start",
                      display: 'flex',
                      flexDirection: selectionMode === "checklist" ? "row" : "column",
                      background: idx === highlightIndex ? '#EEEEEE' : 'inherit',
                      px: 2.5,
                      py: 0.5,
                      '&:hover': {
                        backgroundColor: '#EEEEEE',
                        cursor: 'pointer',
                      },
                      wordBreak: 'break-all',
                      '& .ant-checkbox-inner': {
                        borderColor: '#9F9F9F !important',
                      },
                      '& .ant-checkbox-checked .ant-checkbox-inner': {
                        backgroundColor: '#6D72F6 !important',
                        borderColor: '#6D72F6 !important',
                      },
                      '& .ant-checkbox-indeterminate .ant-checkbox-inner': {
                        backgroundColor: '#6D72F6 !important',
                        borderColor: '#6D72F6 !important',
                      },
                      '& .ant-checkbox-indeterminate .ant-checkbox-inner::after': {
                        backgroundColor: '#FFFFFF !important',
                      },
                    }}
                  >
                    {selectionMode === "checklist" && (
                      <Checkbox
                        checked={allState ? allState === "checked" : chips.includes(s.fullPath)}
                        indeterminate={allState === "indeterminate"}
                        style={{ marginRight: 8, alignSelf: "center" }}
                      />
                    )}
                    <Box
                      sx={{
                        minWidth: 0,
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                      }}
                    >
                    <Typography variant="fw500s12c15191E" sx={{lineHeight: '20px'}}>
                      {s.name}
                    </Typography>
                    <Typography
                      variant="fw400s12c686868"
                      sx={{
                        lineHeight: '20px',
                        display: "block",
                      }}
                    >
                      {s.fullPath}
                    </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Grid2>
          </Box>
        )}
      </Box>
      {(chips.length > 0 || invalidChips.length > 0) && (
        <Box>
          <Box
            onClick={onClearAll}
            sx={{
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              color: '#6D72F6',
              '&:hover svg': {
                color: '#4D44D8'
              },
              '&:hover span': {
                color: '#4D44D8'
              }
            }}
          >
            <CustomIcon name="trash-icon" />
            <Typography variant="fw400s12lh24c6D72F6" sx={{ml: 0.5}}>Clear All</Typography>
          </Box>
        </Box>
      )}

      <Box sx={{ maxHeight: maxHeight, overflowY: 'auto' }}>
        {/* Chips */}
        {(chips.length > 0 || invalidChips.length > 0) && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {chips.map((chip) => (
              <Chip
                key={chip}
                size="small"
                label={
                  <Typography
                    variant="fw400s12c3D4B5A"
                    sx={{
                      lineHeight: '24px',
                      maxWidth: '280px', // slightly less than Chip maxWidth
                      display: 'block',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >{getChipShortForm(chip)}</Typography>
                }
                onDelete={() => handleRemoveChip(chip)}
                title={chip}
                color="primary"
                deleteIcon={
                  <CloseIcon
                    sx={{
                      fontSize: '10px !important',
                      color: "#6D72F6 !important",
                      padding: '3px',
                      borderRadius: '50%',
                      '&:hover': {
                        backgroundColor: '#EAEAEA !important',
                        color: '#930500 !important'
                      }}
                    }
                  />
                }
                sx={{
                  borderRadius: '4px',
                  backgroundColor: '#F3F5F7',
                  maxWidth: '300px',
                  '& .MuiChip-label': {
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                }}
              />
            ))}
            {invalidChips.map((chip) => (
              <Chip
                key={chip}
                size="small"
                label={
                  <Typography
                    variant="fw400s12c3D4B5A"
                    sx={{
                      color: '#B50500',
                      lineHeight: '24px',
                      maxWidth: '280px', // slightly less than Chip maxWidth
                      display: 'block',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >{chip}</Typography>
                }
                onDelete={() => {
                  setInvalidChips((prev) => prev.filter((chipValue) => chipValue !== chip));
                }}
                title={chip}
                color="primary"
                deleteIcon={
                  <CloseIcon
                    sx={{
                      fontSize: '10px !important',
                      color: "#B50500 !important",
                      padding: '3px',
                      borderRadius: '50%',
                      '&:hover': {
                        backgroundColor: '#93050022 !important',
                        color: '#930500 !important'
                      }}
                    }
                  />
                }
                sx={{
                  borderRadius: '4px',
                  backgroundColor: '#FFF1F1',
                  maxWidth: '300px',
                  '& .MuiChip-label': {
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                }}
              />
            ))}
          </Box>
        )}
        {/* Error */}
        {invalidChips.length > 0 && (
          <Typography variant="fw400s10c930500" color="error" sx={{ mt: 0.5, display: "block", lineHeight: '20px' }}>
            * Invalid input. Refer to input guide for instructions
          </Typography>
        )}

        {/* Error */}
        {error && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
            {error}
          </Typography>
        )}
      </Box>
    </Grid2>
  );
}
